import { Mesh, Vector3 } from '@babylonjs/core';
import { AssetObject } from '../AssetObject';
import { Level } from '../Level';
import { AssetObjectConfig, MeshFactory } from '../MeshFactory';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { PlayerCollision } from '../PlayerCollision';
import { ObstacleBehavior } from './ObstacleBehavior';

interface TurretProjectile {
  mesh: Mesh;
  velocity: Vector3;
  lifeTime: number;
}

/**
 * Turret Behavior – advanced cannon with lead-targeting, burst fire, and wind-up rotation.
 *
 * Config fields:
 *   turretRange   – activation range (default 30)
 *   fireInterval  – total cooldown between bursts (default 4)
 *   speed         – projectile speed (default 18)
 *   radius        – burst shot count (reused, default 3)
 */
export class ObstacleTurretBehavior extends ObstacleBehavior {
  private cooldownTimer: number = 0;
  private state: 'idle' | 'aiming' | 'burst' | 'cooldown' = 'idle';
  private aimTimer: number = 0;
  private aimDuration: number = 0.8;
  private burstTimer: number = 0;
  private burstInterval: number = 0.5;
  private shotsRemaining: number = 0;
  private predictedTarget: Vector3 = Vector3.Zero();
  private projectiles: TurretProjectile[] = [];
  private projectileLifeTime: number = 8;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleTurret', startPosition, pathIndex, config);
  }

  initLevel(level: Level): ObstacleTurretBehavior {
    super.initLevel(level);
    return this;
  }

  update(deltaTime: number): void {
    if (!this.level?.player) return;

    const range = this.config.turretRange ?? 30;
    const fireInterval = this.config.fireInterval ?? 4;
    const burstCount = Math.round(this.config.radius ?? 3);
    const dist = this.getPlayerDistance();

    // Update projectiles
    this.updateProjectiles(deltaTime);

    switch (this.state) {
      case 'idle': {
        if (dist <= range) {
          this.state = 'aiming';
          this.aimTimer = 0;
          // Compute lead target
          this.predictedTarget = this.computeLeadTarget();
        }
        break;
      }
      case 'aiming': {
        this.aimTimer += deltaTime;
        // Smoothly rotate toward predicted position
        this.lerpLookAt(this.predictedTarget, deltaTime * 2);
        if (this.aimTimer >= this.aimDuration) {
          this.state = 'burst';
          this.shotsRemaining = burstCount;
          this.burstTimer = 0;
        }
        break;
      }
      case 'burst': {
        this.burstTimer += deltaTime;
        if (this.shotsRemaining > 0 && this.burstTimer >= this.burstInterval) {
          this.fireProjectile();
          this.shotsRemaining--;
          this.burstTimer = 0;
        }
        if (this.shotsRemaining <= 0 && this.burstTimer >= this.burstInterval) {
          this.state = 'cooldown';
          this.cooldownTimer = 0;
        }
        break;
      }
      case 'cooldown': {
        this.cooldownTimer += deltaTime;
        if (this.cooldownTimer >= fireInterval) {
          this.state = dist <= range ? 'aiming' : 'idle';
          this.aimTimer = 0;
          this.predictedTarget = this.computeLeadTarget();
        }
        break;
      }
    }
  }

  /** Compute where player will be in ~0.4 seconds based on current velocity (approximated by recent position) */
  private computeLeadTarget(): Vector3 {
    const playerPos = this.level.player.getPosition();
    // Simple lead: aim slightly ahead in the player's general position with some random jitter
    return playerPos.add(new Vector3((Math.random() - 0.5) * 0.5, 0.5, (Math.random() - 0.5) * 0.5));
  }

  private lerpLookAt(target: Vector3, speed: number): void {
    const dir = target.subtract(this.mesh.position);
    dir.y = 0;
    if (dir.length() > 0.01) {
      const targetAngle = Math.atan2(dir.x, dir.z);
      let delta = targetAngle - this.mesh.rotation.y;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      this.mesh.rotation.y += delta * Math.min(speed, 1);
    }
  }

  private fireProjectile(): void {
    if (!this.level) return;

    const spawnPos = this.mesh.position.clone();
    spawnPos.y += 1.5;

    const dir = this.predictedTarget.subtract(spawnPos).normalize();
    const speed = this.config.speed ?? 18;

    const bombAsset = new AssetObject('obstacleProjectile').withClone(true);
    const cfg = AssetObjectConfig.builder().object(bombAsset).scale(1.5).physicsShapeType(undefined).build();
    const projectileMesh = cfg.buildMesh(this.level) as Mesh;
    MeshFactory.configureMesh(projectileMesh, cfg, this.level);
    projectileMesh.position.copyFrom(spawnPos);

    const p: TurretProjectile = {
      mesh: projectileMesh,
      velocity: dir.scale(speed),
      lifeTime: this.projectileLifeTime,
    };

    projectileMesh.metadata = {
      ...projectileMesh.metadata,
      playerCollision: new PlayerCollision(false, (level, player, event) => {
        if (!player.vulnerable || level.game.config.godMode) {
          this.removeProjectile(this.projectiles.indexOf(p));
        } else {
          player.collide(event, 'Hit by turret!');
          this.removeProjectile(this.projectiles.indexOf(p));
        }
      }),
    };
    this.level.player?.registerCollisionMesh(projectileMesh);
    this.level.game.sound.play('explosion', this.mesh);

    this.projectiles.push(p);
  }

  private updateProjectiles(deltaTime: number): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.lifeTime -= deltaTime;
      if (p.lifeTime <= 0) {
        this.removeProjectile(i);
        continue;
      }
      p.mesh.position.addInPlace(p.velocity.scale(deltaTime));
      p.mesh.rotation.x += 8 * deltaTime;
    }
  }

  private removeProjectile(index: number): void {
    if (index < 0 || index >= this.projectiles.length) return;
    const p = this.projectiles[index];
    this.level?.player?.unregisterCollisionMesh(p.mesh);
    this.level?.particles.createDisappearanceParticleSystem(p.mesh, 0.3);
    this.level?.game.sound.play('enemyprojectilehit', this.mesh);
    p.mesh.dispose();
    this.projectiles.splice(index, 1);
  }

  detach(): void {
    for (const p of this.projectiles) {
      this.level?.player?.unregisterCollisionMesh(p.mesh);
      p.mesh.dispose();
    }
    this.projectiles = [];
    super.detach();
  }

  clone(): ObstacleTurretBehavior {
    return new ObstacleTurretBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
